import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FIELD_TYPE_CATALOGUE,
  diffFields,
  fieldReferences,
  formProblems,
  type FieldDef,
  type FieldType,
  type FormDetail,
  type FormSettings,
  type FormType,
} from '@jfh/intake-shared';
import { api } from '../lib/api.js';
import { INITIAL_FORMS_BASE_URL } from '../lib/deployment.js';
import { Icon } from '../components/Icon.js';
import { EmptyState } from '../components/Primitives.js';
import { useToast } from '../components/Toast.js';
import { FormCanvas, type CanvasView } from '../builder/canvas/FormCanvas.js';
import { QuestionPicker } from '../builder/canvas/QuestionPicker.js';
import { TryItOut } from '../builder/canvas/TryItOut.js';
import {
  insertIndex,
  insertWhere,
  moveQuestion,
  moveStep,
  newQuestion,
  stepIndexOf,
  stepsOf,
  type InsertAt,
} from '../builder/canvas/logic.js';
import { QuestionPanel } from '../builder/panel/QuestionPanel.js';
import { FormSettingsTab } from '../builder/settings/FormSettingsTab.js';
import { ShareDialog } from '../components/ShareDialog.js';
import { PublishReview, type GoTo } from '../builder/PublishReview.js';
import { History } from '../builder/History.js';

type Tab = 'questions' | 'settings' | 'history';
const TAB_LABELS: Record<Tab, string> = {
  questions: 'Questions',
  settings: 'Form settings',
  history: 'History',
};
type SaveState = 'saved' | 'unsaved' | 'saving';

const SAVE_DEBOUNCE = 800;
/** Below this the question panel becomes an overlay, so a Teams tab still fits. */
const NARROW = '(max-width: 1239px)';

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

/**
 * Live-normalise slug input as the user types — mirrors the server's `slugify`
 * but keeps any trailing dash so words can still be separated mid-typing. The
 * server trims/dedupes on save; we re-sync from its response after publish.
 */
function slugInput(v: string): string {
  return v
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .slice(0, 60);
}

export function Builder() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [detail, setDetail] = useState<FormDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Working copy.
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState<FormType>('trade_account');
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [settings, setSettings] = useState<FormSettings>({});

  const [selId, setSelId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('questions');
  const [view, setView] = useState<CanvasView>(0);
  const [trying, setTrying] = useState(false);
  const [picker, setPicker] = useState<InsertAt | null>(null);
  /** Bumped to ask the question panel to confirm deleting (it lists what depends on it). */
  const [deleteAsk, setDeleteAsk] = useState(0);
  const narrow = useNarrow();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [formsBase, setFormsBase] = useState(INITIAL_FORMS_BASE_URL);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [publishing, setPublishing] = useState(false);

  const saveTimer = useRef<number | undefined>(undefined);
  const skipNextSave = useRef(true);

  // Resolve the configured public forms URL (for the Test link + embed snippet).
  useEffect(() => {
    api
      .getFormsBaseUrl()
      .then((r) => setFormsBase(r.url))
      .catch(() => undefined);
  }, []);

  // Load form detail.
  useEffect(() => {
    let cancelled = false;
    api
      .getForm(id)
      .then(({ form }) => {
        if (cancelled) return;
        setDetail(form);
        setName(form.name);
        setSlug(form.slug);
        setType(form.type);
        setFields(form.draftSchema.map((f) => ({ ...f })));
        setSettings({ ...form.settings });
        setSelId(null);
        setView(0);
        skipNextSave.current = true;
        setSaveState('saved');
      })
      .catch((e: unknown) =>
        setLoadError(e instanceof Error ? e.message : 'Failed to load form'),
      );
    return () => {
      cancelled = true;
    };
  }, [id]);

  const runSave = useCallback(async () => {
    setSaveState('saving');
    try {
      const { form } = await api.updateForm(id, {
        name,
        type,
        settings,
        schema: fields,
        // Omit an empty slug so clearing the field to retype never wipes it to "form".
        ...(slug.trim() ? { slug } : {}),
      });
      setDetail(form);
      setSaveState('saved');
    } catch (e: unknown) {
      setSaveState('unsaved');
      toast({
        title: 'Save failed',
        body: e instanceof Error ? e.message : undefined,
        icon: 'x',
      });
    }
  }, [id, name, slug, type, settings, fields, toast]);

  // Debounced autosave whenever the working copy changes.
  useEffect(() => {
    if (!detail) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    setSaveState('unsaved');
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => void runSave(), SAVE_DEBOUNCE);
    return () => window.clearTimeout(saveTimer.current);
    // runSave already depends on the working-copy state.
  }, [name, slug, type, fields, settings, detail, runSave]);

  const sel = fields.find((f) => f.id === selId) ?? null;

  // Draft vs live (F9 P1): what's changed, and what would break if published.
  const changes = useMemo(
    () => (detail?.liveSchema ? diffFields(detail.liveSchema, fields) : []),
    [detail?.liveSchema, fields],
  );
  const problems = useMemo(() => formProblems(fields, settings), [fields, settings]);
  const liveVersion = detail?.versions.find((v) => v.id === detail.currentVersionId)?.version ?? null;

  /** Select a question and show the step (or staff page) it's on. */
  const select = (fieldId: string, within: FieldDef[] = fields) => {
    setSelId(fieldId);
    const k = stepIndexOf(within, fieldId);
    setView(k >= 0 ? k : 'staff');
  };

  const goTo: GoTo = (fieldId, toTab) => {
    setReviewOpen(false);
    setTrying(false);
    if (toTab === 'settings') {
      setTab('settings');
      return;
    }
    setTab('questions');
    if (fieldId) select(fieldId);
  };

  const restoreVersion = (schema: FieldDef[], version: number) => {
    setFields(schema.map((f) => ({ ...f })));
    setSelId(schema[0]?.id ?? null);
    setView(0);
    setTab('questions');
    toast({ title: `Restored v${version} as your draft`, body: 'Nothing is live until you publish.', icon: 'check' });
  };

  /** Add a question of `fieldType` where the picker was opened, and select it. */
  const addField = (fieldType: FieldType, at: InsertAt) => {
    const audience = 'staff' in at || (view === 'staff' && fieldType !== 'heading') ? 'staff' : 'customer';
    const nf = newQuestion(fieldType, audience, fields);
    const next = [...fields];
    next.splice(insertIndex(fields, at, fieldType), 0, nf);
    setFields(next);
    setPicker(null);
    select(nf.id, next);
    const name = FIELD_TYPE_CATALOGUE.find((t) => t.type === fieldType)?.plainName ?? 'Question';
    if (fieldType === 'company_number') {
      const cl = nf.companyLookup;
      toast({
        title: 'Company number added',
        body:
          cl?.nameFieldId && cl.addressFieldId
            ? 'It will fill in and lock the company name and registered address questions.'
            : 'Choose which questions it fills in, on the right.',
        icon: 'badge',
      });
    } else if (fieldType === 'heading') {
      toast({ title: 'Step added', body: 'Name it, then add its questions.', icon: 'plus' });
    } else {
      toast({ title: `${name} added`, body: 'Type the question on the form or on the right.', icon: 'plus' });
    }
  };

  const updateField = (fieldId: string, patch: Partial<FieldDef>) =>
    setFields((fs) => fs.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)));

  const updateSelected = (patch: Partial<FieldDef>) => {
    if (selId) updateField(selId, patch);
  };

  /** Move a question up or down one place (Alt + ↑/↓); follow it into another step. */
  const moveField = (fieldId: string, dir: -1 | 1) => {
    const next = moveQuestion(fields, fieldId, dir);
    if (!next) return;
    setFields(next);
    const k = stepIndexOf(next, fieldId);
    if (k >= 0 && view !== 'staff' && k !== view && next.find((f) => f.id === fieldId)?.type !== 'heading') {
      setView(k);
      toast({ title: `Moved to “${stepsOf(next)[k]?.head?.label ?? 'Your details'}”`, icon: 'chevronR' });
    }
  };

  /** Copy a question just below itself (new id), and select the copy. */
  const duplicateField = (fieldId: string) => {
    const src = fields.find((f) => f.id === fieldId);
    if (!src) return;
    const copy: FieldDef = { ...structuredClone(src), id: crypto.randomUUID().slice(0, 8), label: `${src.label} (copy)` };
    setFields((fs) => {
      const i = fs.findIndex((f) => f.id === fieldId);
      const arr = [...fs];
      arr.splice(i + 1, 0, copy);
      return arr;
    });
    setSelId(copy.id);
  };

  const deleteField = (fieldId: string) => {
    const f = fields.find((x) => x.id === fieldId);
    setFields((fs) => fs.filter((x) => x.id !== fieldId));
    setSelId((cur) => (cur === fieldId ? null : cur));
    if (f) {
      toast({
        title: `Deleted “${f.label}”`,
        body: liveVersion === null ? undefined : 'It stays on the live form until you publish.',
        icon: 'trash',
      });
    }
  };

  /** Delete from the canvas toolbar: straight away, or via the panel's check when something depends on it. */
  const askDelete = (fieldId: string) => {
    if (!fieldReferences(fields, settings, fieldId).length) {
      deleteField(fieldId);
      return;
    }
    setSelId(fieldId);
    setDeleteAsk((n) => n + 1);
  };

  /** Move a whole step; the step being edited stays the one showing. */
  const moveStepTo = (from: number, to: number) => {
    const next = moveStep(fields, from, to);
    if (!next) return;
    const showing = view === 'staff' ? null : (stepsOf(fields)[view]?.head?.id ?? null);
    setFields(next);
    if (showing) setView(stepIndexOf(next, showing));
  };

  const reorder = (dragId: string, targetId: string) => {
    if (dragId === targetId) return;
    setFields((fs) => {
      const arr = [...fs];
      const from = arr.findIndex((f) => f.id === dragId);
      const to = arr.findIndex((f) => f.id === targetId);
      if (from < 0 || to < 0) return fs;
      const [moved] = arr.splice(from, 1);
      if (moved) arr.splice(to, 0, moved);
      return arr;
    });
  };

  const publish = async () => {
    if (fields.length === 0 || publishing || !detail) return;
    setPublishing(true);
    try {
      // Flush any pending edits first so we publish the latest draft.
      window.clearTimeout(saveTimer.current);
      if (saveState !== 'saved') await runSave();
      const publishedVersion = detail.draftVersion;
      const { form } = await api.publishForm(id);
      setReviewOpen(false);
      setDetail(form);
      setSlug(form.slug);
      setType(form.type);
      setSettings({ ...form.settings });
      setSaveState('saved');
      toast({
        title: `Published v${publishedVersion}`,
        body: 'Embedded form updated everywhere instantly',
        icon: 'bolt',
      });
    } catch (e: unknown) {
      toast({ title: 'Publish failed', body: e instanceof Error ? e.message : undefined, icon: 'x' });
    } finally {
      setPublishing(false);
    }
  };

  /** The right-hand column; an overlay on narrow screens. */
  const panel = (content: ReactNode) => (
    <aside
      aria-label="Question settings"
      className="scroll-y"
      style={{
        borderLeft: '1px solid var(--border)',
        background: 'var(--bg-surface-2)',
        ...(narrow
          ? { position: 'absolute', right: 0, top: 0, bottom: 0, width: 380, maxWidth: '100%', zIndex: 20, boxShadow: 'var(--sh-lg)' }
          : {}),
      }}
    >
      {content}
    </aside>
  );

  if (loadError) {
    return (
      <div style={{ padding: 26 }}>
        <button className="btn ghost sm" onClick={() => navigate('/forms')}>
          <Icon name="arrowLeft" size={15} /> Back to forms
        </button>
        <div className="card" style={{ marginTop: 16 }}>
          <EmptyState icon="forms" title="Couldn't load this form" body={loadError} />
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div style={{ padding: 40, color: 'var(--text-3)' }} className="fade-in">
        Loading form…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* builder topbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 20px',
          borderBottom: '1px solid var(--border)',
          flex: 'none',
        }}
      >
        <button className="icon-btn" onClick={() => navigate('/forms')} title="Back to forms">
          <Icon name="arrowLeft" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Form name"
              style={{
                fontWeight: 800,
                fontSize: 16,
                border: '1px solid transparent',
                background: 'transparent',
                color: 'var(--text-1)',
                borderRadius: 6,
                padding: '2px 6px',
                maxWidth: 360,
                outline: 'none',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--border-strong)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'transparent')}
            />
            {liveVersion === null ? (
              <button className="badge pending" style={{ border: 'none' }} onClick={() => setReviewOpen(true)}>
                <span className="dot" /> Draft · not published yet
              </button>
            ) : changes.length > 0 ? (
              <button
                className="badge pending"
                style={{ border: 'none', cursor: 'pointer' }}
                onClick={() => setReviewOpen(true)}
                title="Review the changes and publish"
              >
                <span className="dot" /> Draft · {changes.length} {changes.length === 1 ? 'change' : 'changes'} not
                live yet
              </button>
            ) : (
              <span className="badge approved">
                Live · v{liveVersion} · no unpublished changes
              </span>
            )}
            <span style={{ fontSize: 12, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {saveState === 'saved' ? (
                <>
                  <Icon name="check" size={13} /> Draft saved
                </>
              ) : saveState === 'saving' ? (
                'Saving…'
              ) : (
                'Unsaved'
              )}
            </span>
          </div>
          <div
            className="mono"
            style={{ fontSize: 11.5, color: 'var(--text-3)', paddingLeft: 6, display: 'flex', alignItems: 'center' }}
          >
            <span>{formsBase.replace(/^https?:\/\//, '')}/f/</span>
            <input
              value={slug}
              onChange={(e) => setSlug(slugInput(e.target.value))}
              aria-label="Form address (slug)"
              title="The form's web address. Changing it changes the public/embed URL."
              spellCheck={false}
              className="mono"
              style={{
                border: '1px solid transparent',
                background: 'transparent',
                color: 'var(--text-2)',
                borderRadius: 4,
                padding: '1px 4px',
                fontSize: 11.5,
                width: `${Math.max(slug.length, 6) + 1}ch`,
                outline: 'none',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--border-strong)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'transparent')}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 3,
            background: 'var(--bg-sunken)',
            padding: 3,
            borderRadius: 8,
            marginRight: 4,
          }}
        >
          {(['questions', 'settings', 'history'] as const).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setTrying(false);
              }}
              style={{
                border: 'none',
                background: tab === t ? 'var(--bg-surface)' : 'transparent',
                color: tab === t ? 'var(--text-1)' : 'var(--text-2)',
                fontWeight: 600,
                fontSize: 12.5,
                padding: '5px 12px',
                borderRadius: 6,
                boxShadow: tab === t ? 'var(--sh-xs)' : 'none',
              }}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
        {trying ? (
          <button
            className="btn sm"
            onClick={() => setTrying(false)}
            style={{ borderColor: 'var(--jfh-fern-green)', background: 'var(--surface-brand-tint)' }}
          >
            <Icon name="pencil" size={15} /> Back to editing
          </button>
        ) : (
          <button
            className="btn sm"
            title="Fill in the draft as an applicant would"
            disabled={fields.length === 0}
            onClick={() => {
              setTab('questions');
              setTrying(true);
            }}
          >
            <Icon name="send" size={15} /> Try it out
          </button>
        )}
        <button className="btn sm" onClick={() => setEmbedOpen(true)}>
          <Icon name="link" size={15} /> Share
        </button>
        <button
          className="btn primary sm"
          onClick={() => setReviewOpen(true)}
          disabled={fields.length === 0}
        >
          <Icon name="bolt" size={15} /> Review &amp; publish
        </button>
      </div>

      {tab === 'questions' ? (
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 380px',
            minHeight: 0,
            position: 'relative',
          }}
        >
          {trying ? (
            <TryItOut
              fields={fields}
              formName={name}
              formType={type}
              slug={slug}
              settings={settings}
              onStop={() => setTrying(false)}
              panel={panel}
            />
          ) : (
            <>
              <FormCanvas
                fields={fields}
                formName={name}
                formType={type}
                view={view}
                onView={setView}
                selectedId={selId}
                onSelect={setSelId}
                onUpdate={updateField}
                onMove={moveField}
                onDuplicate={duplicateField}
                onDelete={askDelete}
                onReorder={reorder}
                onInsert={setPicker}
                onAddStep={() => addField('heading', { step: stepsOf(fields).length - 1 })}
                onMoveStep={moveStepTo}
              />
              {(!narrow || sel) &&
                panel(
                  <QuestionPanel
                    key={sel?.id ?? 'none'}
                    field={sel}
                    fields={fields}
                    formType={type}
                    settings={settings}
                    update={updateSelected}
                    onSettingsChange={(patch) => setSettings((s) => ({ ...s, ...patch }))}
                    onClose={() => setSelId(null)}
                    onMove={(dir) => sel && moveField(sel.id, dir)}
                    onDuplicate={() => sel && duplicateField(sel.id)}
                    onDelete={() => sel && deleteField(sel.id)}
                    deleteAsk={deleteAsk}
                  />,
                )}
            </>
          )}
        </div>
      ) : tab === 'settings' ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div
            style={{
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 20px',
              fontSize: 12.5,
              background: 'var(--warning-100)',
              color: 'var(--text-1)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <Icon name="bolt" size={14} />
            These settings save and go live as you change them. Unlike questions, they don't wait for
            Review &amp; publish.
          </div>
          <FormSettingsTab
            type={type}
            settings={settings}
            fields={fields}
            onTypeChange={setType}
            onSettingsChange={(patch) => setSettings((s) => ({ ...s, ...patch }))}
          />
        </div>
      ) : (
        <History
          formId={id}
          versions={detail.versions}
          currentVersionId={detail.currentVersionId}
          draftVersion={detail.draftVersion}
          changeCount={changes.length}
          onReview={() => setReviewOpen(true)}
          onRestore={restoreVersion}
        />
      )}

      <PublishReview
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        problems={problems}
        changes={changes}
        liveVersion={liveVersion}
        draftVersion={detail.draftVersion}
        publishing={publishing}
        onPublish={() => void publish()}
        onGoTo={goTo}
      />

      <QuestionPicker
        open={picker !== null}
        where={picker ? insertWhere(fields, picker) : ''}
        staffView={picker !== null && 'staff' in picker}
        onPick={(t) => picker && addField(t, picker)}
        onClose={() => setPicker(null)}
      />

      <ShareDialog
        target={embedOpen ? { name, slug: detail.slug, live: liveVersion !== null && !detail.archivedAt } : null}
        baseUrl={formsBase}
        onClose={() => setEmbedOpen(false)}
      />
    </div>
  );
}
